import React, { useState, useEffect } from "react";
import moment from "moment";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const PAGE_SIZE = 5;
const OTHER_LABELS = ["other", "others"];

const isResolved = (status) => {
    const s = (status || "").toUpperCase();
    return s === "CLOSE" || s === "CLOSED" || s === "RESOLVED";
};

const getDisplayType = (item) => {
    const issueName = item.issue?.name || "";
    const customType = item.type || "";
    if (OTHER_LABELS.includes(issueName.toLowerCase())) {
        return customType ? `${customType} (${issueName})` : issueName;
    }
    return issueName || customType || "Issue";
};

const YourIssues = () => {
    const navigate = useNavigate();
    const [issues, setIssues] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState(null);
    const [pageNo, setPageNo] = useState(1);
    const [pagination, setPagination] = useState({});

    const [detailOpen, setDetailOpen] = useState(false);
    const [selectedIssue, setSelectedIssue] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailError, setDetailError] = useState(null);

    const [feedbackOpen, setFeedbackOpen] = useState(false);
    const [selectedIssueId, setSelectedIssueId] = useState(null);
    const [feedbackText, setFeedbackText] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const [imageViewer, setImageViewer] = useState({ open: false, url: null });

    const fetchIssues = async (page = 1) => {
        try {
            setError(null);
            const res = await axios.get(
                `${process.env.REACT_APP_SERVER_ADDRESS}/common/other/show-feedback`,
                {
                    params: { size: PAGE_SIZE, pageNo: page },
                },
            );
            setIssues(res?.data?.data || []);
            setPagination(res?.data?.pagination || {});
            setPageNo(page);
        } catch {
            setError("Unable to load your issues. Please try again.");
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    };

    const fetchDetail = async (id) => {
        const res = await axios.get(
            `${process.env.REACT_APP_SERVER_ADDRESS}/common/other/show-feedback`,
            {
                params: { feedbackId: id },
            },
        );
        return res?.data?.data;
    };

    const reopenFeedback = async (feedbackId, reopeningMsg) => {
        const res = await axios.patch(
            `${process.env.REACT_APP_SERVER_ADDRESS}/common/other/reopen-feedback`,
            {
                feedbackId,
                reopeningMsg,
            },
        );
        return res?.data;
    };

    useEffect(() => {
        fetchIssues(1);
    }, []);

    const openDetail = async (item) => {
        setSelectedIssue(item);
        setDetailOpen(true);
        setDetailError(null);
        setDetailLoading(true);
        try {
            const full = await fetchDetail(item.id);
            if (full) setSelectedIssue((prev) => ({ ...prev, ...full }));
        } catch {
            setDetailError("Unable to load full details.");
        } finally {
            setDetailLoading(false);
        }
    };

    const handleReopen = (issueId) => {
        setDetailOpen(false);
        if (
            window.confirm(
                "Are you not satisfied with the resolution? Provide issues to reopen.",
            )
        ) {
            setSelectedIssueId(issueId);
            setFeedbackText("");
            setFeedbackOpen(true);
        }
    };

    const submitReopen = async () => {
        if (!feedbackText.trim()) {
            alert(
                "Please describe why you are not satisfied before reopening.",
            );
            return;
        }
        setSubmitting(true);
        try {
            await reopenFeedback(selectedIssueId, feedbackText.trim());
            setIssues((prev) =>
                prev.map((i) =>
                    i.id === selectedIssueId ? { ...i, status: "OPEN" } : i,
                ),
            );
            alert("Issue has been reopened successfully.");
        } catch (err) {
            alert(err?.response?.data?.message || "Something went wrong.");
        } finally {
            setSubmitting(false);
            setFeedbackOpen(false);
            setSelectedIssueId(null);
            setFeedbackText("");
        }
    };

    const detailResolved = selectedIssue
        ? isResolved(selectedIssue.status)
        : false;

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen gap-4 bg-gray-50">
                <div className="w-10 h-10 border-4 border-gray-200 border-t-yellow-500 rounded-full animate-spin" />
                <p className="text-sm text-gray-500">Loading your issues…</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen gap-4 bg-gray-50">
                <span className="text-5xl">⚠️</span>
                <p className="text-sm text-red-500">{error}</p>
                <button
                    onClick={() => fetchIssues(1)}
                    className="px-6 py-2.5 bg-yellow-400 hover:bg-yellow-500 text-black text-sm font-semibold rounded-lg transition"
                >
                    Retry
                </button>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 py-6 px-4 md:px-8 lg:px-12 font-sans">
            <div className="max-w-7xl mx-auto mb-8 flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => window.history.back()}
                        className="w-9 h-9 flex items-center justify-center bg-white rounded-full shadow-sm text-lg hover:bg-gray-100 transition text-black"
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-6 w-6"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2}
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M15 19l-7-7 7-7"
                            />
                        </svg>
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold text-black">
                            Your Issues
                        </h1>
                        <p className="text-sm text-gray-600">
                            All messages are sent directly to HR.
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate("/layout/emp/write-to-hr")}
                        className="px-5 py-2.5 bg-yellow-400 hover:bg-yellow-500 text-black text-sm font-semibold rounded-lg shadow-sm transition flex items-center gap-1.5"
                    >
                        <span>＋</span> Add Your Issue
                    </button>
                    {issues.length > 0 && (
                        <span className="text-sm text-gray-500 bg-white px-4 py-2 rounded-full shadow-sm">
                            {pagination?.totalItems || issues.length} total
                        </span>
                    )}
                </div>
            </div>

            {issues.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 gap-4 bg-white rounded-2xl shadow-sm max-w-3xl mx-auto">
                    <span className="text-7xl">📋</span>
                    <p className="text-lg text-gray-400">
                        No issues registered
                    </p>
                    <p className="text-sm text-gray-300">
                        When you raise an issue, it will appear here.
                    </p>
                </div>
            ) : (
                <>
                    {/* Desktop Table */}
                    <div className="hidden sm:block max-w-7xl mx-auto bg-white rounded-2xl shadow-sm overflow-hidden border border-gray-200">
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm text-left">
                                <thead className="bg-gray-50 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    <tr>
                                        <th className="px-6 py-4">Issue</th>
                                        <th className="px-6 py-4">Status</th>
                                        <th className="px-6 py-4">
                                            Description
                                        </th>
                                        <th className="px-6 py-4 text-center">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {issues.map((item) => {
                                        const resolved = isResolved(
                                            item.status,
                                        );
                                        const displayType =
                                            getDisplayType(item);
                                        return (
                                            <tr
                                                key={item.id}
                                                onClick={() => openDetail(item)}
                                                className="hover:bg-gray-50 cursor-pointer transition"
                                            >
                                                <td className="px-6 py-4 font-medium text-black">
                                                    {displayType}
                                                </td>
                                                <td className="px-6 py-4">
                                                    <span
                                                        className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold ${
                                                            resolved
                                                                ? "bg-green-100 text-green-700"
                                                                : "bg-amber-100 text-amber-700"
                                                        }`}
                                                    >
                                                        {resolved
                                                            ? "Resolved"
                                                            : "Pending"}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 text-gray-600 max-w-xs truncate">
                                                    {item.body}
                                                </td>
                                                <td className="px-6 py-4 text-center">
                                                    {resolved && (
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleReopen(
                                                                    item.id,
                                                                );
                                                            }}
                                                            className="inline-flex items-center gap-1.5 text-sm font-medium text-black bg-yellow-400 hover:bg-yellow-500 rounded-lg px-4 py-2 transition"
                                                        >
                                                            🔄 Reopen
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Mobile Cards */}
                    <div className="sm:hidden max-w-7xl mx-auto grid grid-cols-1 gap-6">
                        {issues.map((item) => {
                            const resolved = isResolved(item.status);
                            const displayType = getDisplayType(item);
                            return (
                                <div
                                    key={item.id}
                                    onClick={() => openDetail(item)}
                                    className="bg-white rounded-2xl shadow-md hover:shadow-lg transition-shadow duration-200 p-5 border-l-4 cursor-pointer"
                                    style={{
                                        borderLeftColor: resolved
                                            ? "#22c55e"
                                            : "#f59e0b",
                                    }}
                                >
                                    <div className="flex items-start justify-between mb-2">
                                        <h3 className="text-lg font-semibold text-black leading-tight">
                                            {displayType}
                                        </h3>
                                        <span
                                            className={`text-xs font-bold text-white px-3 py-1 rounded-full whitespace-nowrap ml-2 ${
                                                resolved
                                                    ? "bg-green-500"
                                                    : "bg-amber-500"
                                            }`}
                                        >
                                            {resolved ? "Resolved" : "Pending"}
                                        </span>
                                    </div>

                                    <p className="text-sm text-gray-600 leading-relaxed line-clamp-3 mb-3">
                                        {item.body}
                                    </p>

                                    <div className="flex items-center justify-between text-xs text-gray-400">
                                        <span>
                                            {item.createdAt
                                                ? moment(item.createdAt).format(
                                                      "DD MMM YYYY",
                                                  )
                                                : "—"}
                                        </span>
                                        <span>ID: {item.id}</span>
                                    </div>

                                    {resolved && (
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleReopen(item.id);
                                            }}
                                            className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-black bg-yellow-400 hover:bg-yellow-500 rounded-lg px-4 py-2 transition"
                                        >
                                            🔄 Not satisfied? Reopen
                                        </button>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* Pagination */}
                    <div className="max-w-7xl mx-auto mt-8 flex flex-wrap items-center justify-between gap-4 bg-white px-6 py-4 rounded-2xl shadow-sm border border-gray-200">
                        <button
                            disabled={!pagination?.hasPrevPage || loadingMore}
                            onClick={() => {
                                setLoadingMore(true);
                                fetchIssues(pageNo - 1);
                            }}
                            className={`flex items-center gap-1 px-5 py-2.5 rounded-lg border text-sm font-medium transition
                ${
                    pagination?.hasPrevPage
                        ? "border-yellow-400 text-yellow-600 hover:bg-yellow-50"
                        : "border-gray-200 text-gray-300 cursor-not-allowed opacity-60"
                }`}
                        >
                            ← Previous
                        </button>
                        <span className="text-sm text-gray-600 font-medium">
                            Page {pagination?.page || pageNo} of{" "}
                            {pagination?.totalPages || 1}
                        </span>
                        <button
                            disabled={!pagination?.hasNextPage || loadingMore}
                            onClick={() => {
                                setLoadingMore(true);
                                fetchIssues(pageNo + 1);
                            }}
                            className={`flex items-center gap-1 px-5 py-2.5 rounded-lg border text-sm font-medium transition
                ${
                    pagination?.hasNextPage
                        ? "border-yellow-400 text-yellow-600 hover:bg-yellow-50"
                        : "border-gray-200 text-gray-300 cursor-not-allowed opacity-60"
                }`}
                        >
                            Next →
                        </button>
                    </div>
                </>
            )}

            {/* ── Detail Modal ── */}
            {detailOpen && selectedIssue && (
                <div
                    className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
                    onClick={() => setDetailOpen(false)}
                >
                    <div
                        className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col p-6 md:p-8"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-start justify-between mb-4">
                            <div>
                                <h2 className="text-xl font-bold text-black">
                                    {getDisplayType(selectedIssue)}
                                </h2>
                                <p className="text-sm text-gray-500">
                                    ID: {selectedIssue.id}
                                </p>
                            </div>
                            <span
                                className={`text-xs font-bold text-white px-4 py-1.5 rounded-full ${
                                    detailResolved
                                        ? "bg-green-500"
                                        : "bg-amber-500"
                                }`}
                            >
                                {detailResolved ? "Resolved" : "Pending"}
                            </span>
                        </div>

                        {detailLoading ? (
                            <div className="flex justify-center py-12">
                                <div className="w-8 h-8 border-4 border-gray-200 border-t-yellow-500 rounded-full animate-spin" />
                            </div>
                        ) : detailError ? (
                            <p className="text-sm text-red-500 py-4">
                                {detailError}
                            </p>
                        ) : (
                            <div className="overflow-y-auto flex-1 space-y-5 pr-2">
                                <Field
                                    label="Type"
                                    value={getDisplayType(selectedIssue)}
                                />
                                <Field
                                    label="Description"
                                    value={selectedIssue.body}
                                />
                                <Field
                                    label="Raised on"
                                    value={
                                        selectedIssue.createdAt
                                            ? moment(
                                                  selectedIssue.createdAt,
                                              ).format("DD MMM YYYY, h:mm A")
                                            : "-"
                                    }
                                />
                                {selectedIssue.isReopened && (
                                    <Field
                                        label="Reopen count"
                                        value={selectedIssue.reopenCount}
                                    />
                                )}

                                {selectedIssue.attachments?.length > 0 && (
                                    <div>
                                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                                            Attachments
                                        </p>
                                        <div className="flex flex-wrap gap-3">
                                            {selectedIssue.attachments.map(
                                                (att) => (
                                                    <img
                                                        key={att.id}
                                                        src={att.attachment_url}
                                                        alt="attachment"
                                                        onClick={() =>
                                                            setImageViewer({
                                                                open: true,
                                                                url: att.attachment_url,
                                                            })
                                                        }
                                                        className="w-28 h-28 rounded-xl object-cover border border-gray-200 cursor-pointer hover:opacity-80 transition"
                                                    />
                                                ),
                                            )}
                                        </div>
                                    </div>
                                )}

                                {selectedIssue.trackHistory?.length > 0 && (
                                    <div>
                                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                                            History
                                        </p>
                                        <div className="space-y-2 bg-gray-50 rounded-xl p-3">
                                            {selectedIssue.trackHistory.map(
                                                (entry, i) => (
                                                    <div
                                                        key={i}
                                                        className="border-b border-gray-200 last:border-0 pb-2 last:pb-0"
                                                    >
                                                        <p className="text-sm font-medium text-gray-800">
                                                            {entry.remark ||
                                                                entry.event}
                                                        </p>
                                                        {/* ✅ FIX: only render payload if it's a non-empty string */}
                                                        {typeof entry.payload ===
                                                            "string" &&
                                                            entry.payload.trim() && (
                                                                <p className="text-sm text-gray-600">
                                                                    {
                                                                        entry.payload
                                                                    }
                                                                </p>
                                                            )}
                                                        <p className="text-xs text-gray-400">
                                                            {entry?.userRole} ·{" "}
                                                            {moment(
                                                                entry.timestamp,
                                                            ).format(
                                                                "DD MMM, h:mm A",
                                                            )}
                                                        </p>
                                                    </div>
                                                ),
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="flex flex-col sm:flex-row gap-3 mt-6 pt-4 border-t border-gray-100">
                            <button
                                onClick={() => setDetailOpen(false)}
                                className="flex-1 py-3 rounded-xl border border-gray-300 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition"
                            >
                                Close
                            </button>
                            {detailResolved && (
                                <button
                                    onClick={() =>
                                        handleReopen(selectedIssue.id)
                                    }
                                    className="flex-1 py-3 rounded-xl bg-yellow-400 hover:bg-yellow-500 text-black text-sm font-semibold transition"
                                >
                                    Reopen Issue
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Reopen Feedback Modal */}
            {feedbackOpen && (
                <div
                    className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
                    onClick={() => setFeedbackOpen(false)}
                >
                    <div
                        className="bg-white rounded-3xl shadow-2xl w-full max-w-lg p-6 md:p-8"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h3 className="text-xl font-bold text-black mb-1">
                            Reopen Issue
                        </h3>
                        <p className="text-sm text-gray-500 mb-4">
                            Tell us why you are not satisfied with the
                            resolution. This helps us improve.
                        </p>
                        <textarea
                            rows={4}
                            placeholder="Write your reason here…"
                            value={feedbackText}
                            onChange={(e) => setFeedbackText(e.target.value)}
                            disabled={submitting}
                            className="w-full border border-gray-200 rounded-xl p-3 text-sm text-black bg-gray-50 resize-none focus:outline-none focus:ring-2 focus:ring-yellow-400"
                        />
                        <div className="flex flex-col sm:flex-row gap-3 mt-5">
                            <button
                                onClick={() => {
                                    setFeedbackOpen(false);
                                    setFeedbackText("");
                                }}
                                disabled={submitting}
                                className="flex-1 py-3 rounded-xl border border-gray-300 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={submitReopen}
                                disabled={submitting || !feedbackText.trim()}
                                className={`flex-1 py-3 rounded-xl bg-yellow-400 hover:bg-yellow-500 text-black text-sm font-semibold transition
                  ${!feedbackText.trim() ? "opacity-50 cursor-not-allowed" : ""}`}
                            >
                                {submitting ? "Submitting…" : "Reopen"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Image Viewer */}
            {imageViewer.open && (
                <div
                    className="fixed inset-0 bg-black/95 flex items-center justify-center z-[100] p-4"
                    onClick={() => setImageViewer({ open: false, url: null })}
                >
                    <button
                        className="absolute top-4 right-6 text-white text-3xl font-bold hover:opacity-70 transition"
                        onClick={() =>
                            setImageViewer({ open: false, url: null })
                        }
                    >
                        ✕
                    </button>
                    <img
                        src={imageViewer.url}
                        alt="full view"
                        className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
                    />
                </div>
            )}
        </div>
    );
};

const Field = ({ label, value }) => (
    <div>
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">
            {label}
        </p>
        <p className="text-sm text-black leading-relaxed">{value}</p>
    </div>
);

export default YourIssues;